import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getItemToken } from '../Utils/localStorageUtils';

// Waits until the service worker reaches 'activated' state.
// getToken() throws AbortError if the SW is still in 'installing' state.
// Also handles 'redundant' (failed install) to avoid an infinite hang.
const waitForSWActive = (registration) =>
    new Promise((resolve, reject) => {
        if (registration.active) {
            resolve(registration);
            return;
        }
        const sw = registration.installing || registration.waiting;
        if (!sw) {
            resolve(registration);
            return;
        }
        const onStateChange = (e) => {
            if (e.target.state === 'activated') {
                sw.removeEventListener('statechange', onStateChange);
                resolve(registration);
            } else if (e.target.state === 'redundant') {
                sw.removeEventListener('statechange', onStateChange);
                reject(new Error('Service worker installation failed'));
            }
        };
        sw.addEventListener('statechange', onStateChange);
    });

const useFCMToken = () => {
    const location = useLocation();
    const [token, setToken] = useState(null);
    // Check if Notification API is available before accessing it
    const [notificationPermission, setNotificationPermission] = useState(
        typeof window !== 'undefined' && 'Notification' in window
            ? Notification.permission
            : 'unsupported'
    );

    useEffect(() => {
        const requestPermissionAndGetToken = async (fromUserGesture = false) => {
            // Check all three APIs required for Web Push
            if (typeof window === 'undefined' || !('Notification' in window)) {
                console.warn('[FCM] Notification API not supported (Safari iOS < 16.4 or old browser)');
                return;
            }
            if (!('serviceWorker' in navigator)) {
                console.warn('[FCM] Service Workers not supported in this browser');
                return;
            }
            if (!('PushManager' in window)) {
                console.warn('[FCM] Push API not supported in this browser (Safari < 16, or Brave with Google services disabled)');
                return;
            }

            // Permission gate — NEVER auto-prompt on page load. Chrome permanently auto-blocks a
            // site once the permission prompt is shown and ignored a few times, after which
            // Notification.requestPermission() silently returns 'denied' and NO code can re-prompt
            // (the user must reset it via the site-info icon in the address bar). So only ask on an
            // explicit user gesture (the navbar bell). On load we just re-sync the token for users
            // who already granted permission.
            let permission = Notification.permission;
            if (permission === 'default' && !fromUserGesture) {
                return;
            }

            // Check for access token to ensure user is logged in before registering
            const authToken = localStorage.getItem('access_token');
            // We can register service worker without auth, but we only store token in DB if auth exists.
            try {
                // Fetch dynamic settings
                const settingsResponse = await axios.get(`${process.env.REACT_APP_Baseurl}/Front-End/Push-Notification-Config`);
                const pushConfig = settingsResponse?.data?.push_notification_config;
                const dynamicVapidKey = pushConfig?.vapid_public_key;
                const firebaseConfig = pushConfig?.firebase_config;

                if (!firebaseConfig) {
                    console.error('[FCM] firebase_config is missing from Push Notification Settings. Configure it in the admin panel.');
                    return;
                }

                if (!dynamicVapidKey) {
                    console.error('[FCM] vapid_public_key is missing from Push Notification Settings. This causes "Registration failed - push service error". Add the VAPID key from Firebase Console → Project Settings → Cloud Messaging → Web Push certificates.');
                    return;
                }

                // Initialize Firebase dynamically
                const { initializeApp, getApps, deleteApp } = await import('firebase/app');
                const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

                let messaging;
                const existingApps = getApps();
                if (existingApps.length === 0) {
                    const app = initializeApp(firebaseConfig);
                    messaging = getMessaging(app);
                } else {
                    // If the Firebase project changed (different account), delete the old app
                    // so tokens are re-issued for the correct project. Stale tokens from the old
                    // project are rejected by the new project's Firebase Admin SDK.
                    const currentApp = existingApps[0];
                    if (currentApp.options.projectId !== firebaseConfig.projectId) {
                        await deleteApp(currentApp);
                        localStorage.removeItem('last_synced_fcm_token');
                        const app = initializeApp(firebaseConfig);
                        messaging = getMessaging(app);
                    } else {
                        messaging = getMessaging();
                    }
                }

                // We only reach this point on a user gesture (or when already granted). Prompt
                // only if still 'default'; an already-granted permission resolves without any UI.
                if (permission === 'default') {
                    permission = await Notification.requestPermission();
                }
                setNotificationPermission(permission);

                if (permission === 'granted') {
                    console.log('Notification permission granted.');

                    // Register and wait for activation before calling getToken().
                    // Calling getToken() while the SW is still in 'installing' state causes
                    // "AbortError: Registration failed - push service error" on production.
                    const swUrl = `/firebase-messaging-sw.js?base_url=${encodeURIComponent(process.env.REACT_APP_Baseurl)}`;
                    const swRegistration = await navigator.serviceWorker.register(swUrl);
                    await waitForSWActive(swRegistration);

                    let currentToken;
                    try {
                        currentToken = await getToken(messaging, {
                            vapidKey: dynamicVapidKey,
                            serviceWorkerRegistration: swRegistration
                        });
                    } catch (tokenErr) {
                        if (tokenErr.name === 'AbortError') {
                            // Two causes:
                            // 1. Stale push subscription from an old VAPID key — unsubscribe and retry.
                            // 2. Brave browser with "Use Google services for push messaging" disabled
                            //    in brave://settings/privacy — in that case retry will also fail;
                            //    nothing we can do programmatically for that case.
                            const staleSub = await swRegistration.pushManager.getSubscription();
                            if (staleSub) {
                                await staleSub.unsubscribe();
                                localStorage.removeItem('fcm_token');
                                localStorage.removeItem('last_synced_fcm_token');
                                currentToken = await getToken(messaging, {
                                    vapidKey: dynamicVapidKey,
                                    serviceWorkerRegistration: swRegistration
                                });
                            } else {
                                // No stale subscription — likely Brave blocking push or invalid VAPID key.
                                console.warn('[FCM] Push registration failed with no existing subscription. If using Brave, enable "Use Google services for push messaging" in brave://settings/privacy. Otherwise verify VAPID key in admin settings.');
                            }
                        } else {
                            throw tokenErr;
                        }
                    }

                    if (currentToken) {
                        console.log('FCM Token:', currentToken);
                        setToken(currentToken);
                        localStorage.setItem('fcm_token', currentToken);

                        // Send token to backend
                        await sendTokenToBackend(currentToken);
                    } else {
                        console.log('No registration token available.');
                    }
                } else {
                    console.log('Notification permission denied.');
                }

                // Listen for foreground messages
                const unsubscribe = onMessage(messaging, (payload) => {
                    console.log('Message received in foreground:', payload);

                    const videoUrl = payload?.data?.video_url;

                    if (payload.notification) {
                        navigator.serviceWorker.ready.then((registration) => {
                            registration.showNotification(payload.notification.title, {
                                body: payload.notification.body,
                                icon: payload.notification.image || payload.notification.imageUrl,
                                data: {
                                    video_url: videoUrl
                                }
                            });
                        });
                    }
                });

                return () => unsubscribe();
            } catch (err) {
                console.error('An error occurred while retrieving token:', err);
            }
        };

        const isPushEnabled = localStorage.getItem('push_enabled') !== 'false';
        if (isPushEnabled) {
            requestPermissionAndGetToken();
        }
        const handleToggle = () => {
            const enabled = localStorage.getItem('push_enabled') !== 'false';
            if (enabled) {
                // Fired by the navbar bell click → this IS a user gesture, so prompting is allowed.
                requestPermissionAndGetToken(true);
            }
        };
        window.addEventListener('push_notification_change', handleToggle);
        return () => {
            window.removeEventListener('push_notification_change', handleToggle);
        };
    }, [localStorage.getItem('access_token')]);

    const sendTokenToBackend = async (fcmToken) => {
        try {
            const authHeaders = getItemToken('access_token');

            if (!authHeaders) {
                console.log('User not logged in, skipping device storage');
                return;
            }

            console.log('Sending token to backend with headers:', authHeaders);
            const lastSyncedToken = localStorage.getItem('last_synced_fcm_token');
            if (lastSyncedToken === fcmToken) {
                console.log('Token already synced, skipping API call');
                return;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_Baseurl}/Front-End/update-fcm-token`,
                { device_token: fcmToken },
                { headers: authHeaders }
            );

            localStorage.setItem('last_synced_fcm_token', fcmToken);
            console.log('Token sent to backend:', response.data);
        } catch (error) {
            console.error('Error sending token to backend:', error);
        }
    };

    return { token, notificationPermission };
};

export default useFCMToken;
